import { HistoryEduIcon } from '@monetize/ui/icons';
import { MdFormatQuote } from 'react-icons/md';
import { ISettingsSideNavSection } from '../../../routes/Settings/SideNavBar/settingsNavType';
import { ROLES, ROUTES } from '../../../constants';
import { CommandPaletteItem, ExtraNavItem } from './commandPalette.types';

function flattenSections(
  sections: ISettingsSideNavSection[],
  route: string,
  category: string,
  idPrefix: string,
): CommandPaletteItem[] {
  return sections.flatMap(({ key: sectionKey, label, hash, children }) => {
    const id = `${idPrefix}-${sectionKey}`;
    const item: CommandPaletteItem = {
      id,
      label,
      description: '',
      category,
      route: hash ? `${route}#${hash}` : route,
      sectionKey,
    };
    const childItems = children
      ? flattenSections(children, route, `${category} › ${label}`, id)
      : [];
    return [item, ...childItems];
  });
}

export const buildSettingsCommandItems = (
  settingsNavRoutes: {
    title: string;
    children: {
      key: string;
      title: string;
      description: string;
      route: string;
      isComingSoon?: boolean;
      sections?: ISettingsSideNavSection[];
    }[];
  }[],
): CommandPaletteItem[] =>
  settingsNavRoutes.flatMap((group) =>
    group.children
      .filter(({ isComingSoon }) => !isComingSoon)
      .flatMap(({ key, title, description, route, sections }) => {
        const pageItem: CommandPaletteItem = {
          id: `settings-${key}`,
          label: title,
          description,
          category: `Settings › ${group.title}`,
          route,
        };

        const sectionItems = flattenSections(
          sections ?? [],
          route,
          `Settings › ${group.title} › ${title}`,
          `settings-${key}-section`,
        );

        return [pageItem, ...sectionItems];
      }),
  );

/**
 * Groups results by category, preserving the order in which each category first
 * appears. Flattening the values yields the on-screen order — keyboard navigation
 * must index into that flattened order so the active item follows the visible list.
 */
export const groupResultsByCategory = (
  results: CommandPaletteItem[],
): Map<string, CommandPaletteItem[]> => {
  const groups = new Map<string, CommandPaletteItem[]>();
  for (const item of results) {
    const existing = groups.get(item.category);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(item.category, [item]);
    }
  }
  return groups;
};

export const EXTRA_NAV_ITEMS: ExtraNavItem[] = [
  {
    id: 'nav-Offerings',
    label: 'Offerings',
    route: ROUTES.OFFERING_LIST,
    icon: HistoryEduIcon,
    roles: ROLES.OFFERING_LIST_ROLES,
  },
  {
    id: 'nav-Discounts',
    label: 'Discounts',
    route: ROUTES.DISCOUNT_LIST,
    icon: HistoryEduIcon,
    roles: ROLES.DISCOUNT_LIST_ROLES,
  },
  {
    id: 'nav-Usage Types',
    label: 'Usage Types',
    route: ROUTES.USAGE_TYPE_LIST,
    icon: HistoryEduIcon,
    roles: ROLES.USAGE_TYPE_LIST_ROLES,
  },
  {
    id: 'nav-Buckets',
    label: 'Buckets',
    route: ROUTES.BUCKET_LIST,
    icon: HistoryEduIcon,
    roles: ROLES.BUCKET_LIST_ROLES,
  },
  {
    id: 'nav-Opportunities',
    label: 'Opportunities',
    route: `${ROUTES.SALES_LANDING}/${ROUTES.SALES_OPPORTUNITIES}`,
    icon: MdFormatQuote,
    roles: ROLES.QUOTES_ROLES,
  },
  {
    id: 'nav-Contracts',
    label: 'Contracts',
    route: ROUTES.SALES_CONTRACTS_ROUTE,
    icon: MdFormatQuote,
    roles: ROLES.QUOTES_ROLES,
  },
];
